import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import handleClearError from "../../../core/methods/handleClearError";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import ErrorMessage from "../../../core/common/ErrorMessage";
import useApi from "../../../core/customhook/useApi";
import webservice_url from "../../../core/webservice/Webservice_url";
import { toast } from "react-toastify";

// ── Types ──────────────────────────────────────────────────────────────────────

interface ForgotFormType {
  email: string;
  otp: string;
}

const ForgotPassword = () => {
  const routes = all_routes;
  const navigate = useNavigate();

  const [formData, setFormData] = useState<ForgotFormType>({
    email: "",
    otp: "",
  });
  const [errors, setErrors] = useState<Record<string, string> | null>(null);
  const [step, setStep] = useState<"email" | "otp">("email");
  const [demoOtp, setDemoOtp] = useState("");

  // API
  const forgotPasswordAPI = useApi<any>(webservice_url.forgot_password, {
    method: "POST",
    autoCall: false,
    handleGlobalError: false,
  });

  const verifyOtpAPI = useApi<any>(webservice_url.verify_forgot_otp, {
    method: "POST",
    autoCall: false,
    handleGlobalError: false,
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value?.trim() }));
    handleClearError(setErrors, name);
  };

  // ── validation ────────────────────────────────────────────────────────────────

  const validateEmail = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.email?.trim()) {
      newErrors.email = "Email is required";
    } else if (
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)
    ) {
      newErrors.email = "Enter a valid email address";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateOtp = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.otp?.trim()) {
      newErrors.otp = "Please enter OTP";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ---- handleSendOtp ----
  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateEmail()) return;

    try {
      const res = await forgotPasswordAPI.callApi({
        email: formData?.email ?? "",
      });
      if (res?.success) {
        setStep("otp");
        setDemoOtp(res?.data?.otp);
      }
    } catch (error: any) {
      const errorMsg = error?.response?.data?.error ?? "Invalid Credentials";
      toast.error(errorMsg);
    }
  };

  // ---- handle otp verify  ----
  const handleOTPVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateOtp()) return;

    try {
      const res = await verifyOtpAPI.callApi({
        email: formData?.email ?? "",
        otp: formData?.otp?.toString() ?? "",
      });
      if (res?.success) {
        navigate(routes.resetPassword, {
          replace: true,
          state: {
            passemail: formData.email,
          },
        });
      }
    } catch (error: any) {
      const errorMsg = error?.response?.data?.error ?? "Invalid OTP";
      toast.error(errorMsg);
    }
  };

  return (
    <div className="container-fluid vh-100 overflow-hidden p-0">
      <div className="row h-100 g-0">
 
        <div className="col-lg-7 d-none d-lg-flex align-items-center justify-content-center position-relative">
          <div className="position-absolute top-0 start-0 h-100 w-100 opacity-25" style={{ border: "none", background: "black" }}></div>
          <ImageWithBasePath
            src="assets/img/login_image.png"
            alt="Img"
            className="object-fit-cover w-100 h-100 position-relative"
          />
        </div>

        {/* Right panel - login form - full width below lg, col-lg-5 on lg+ */}
        <div className="col-12 col-lg-5 h-100 overflow-auto d-flex align-items-center justify-content-center ">
          <div className="col-11 col-sm-8 col-md-6 col-lg-10 py4 ">
            {/* login content */}
            <div>
              {/* logo */}
              <div className="mx-auto mt-3" style={{ width: 160 }}>
                <ImageWithBasePath
                  src="/assets/img/logo/AupTech.svg"
                  className="img-fluid"
                  alt="Logo"
                />
              </div>
              {/* form card */}
              <div>
                <div className="text-center mt-1 mb-3">
                  <h2 className="mb-2">Forgot Password</h2>
                  <p className="mb-0 mt-2">
                    {step === "email" ? (
                      "Enter your registered email to receive an OTP"
                    ) : step === "otp" ? (
                      <>
                        A 6-digit OTP has been sent to&nbsp; "
                        <strong>{formData.email}</strong>"
                      </>
                    ) : (
                      ""
                    )}
                  </p>
                </div>

                <div>
                  <div className="mb-3">
                    <label className="form-label">Email Address</label>
                    <div className="input-group mb-1">
                      <input
                        type="email"
                        className="form-control border-end-0"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        disabled={step === "otp"}
                        placeholder="Enter your email"
                      />
                      <span className="input-group-text border-start-0">
                        <i className="ti ti-mail" />
                      </span>
                    </div>
                    <ErrorMessage message={errors?.email} />
                  </div>

                  {/* OTP field — visible only after email step */}
                  {step === "otp" && (
                    <>
                      <div className="mb-2">
                        <label className="form-label">Enter OTP</label>

                        <input
                          type="text"
                          inputMode="numeric"
                          className="form-control mb-1"
                          name="otp"
                          value={formData.otp}
                          placeholder="Enter 6-digit OTP"
                          onChange={(e) => {
                            const val = e.target.value
                              .replace(/\D/g, "")
                              .slice(0, 6);
                            setFormData((prev) => ({ ...prev, otp: val }));
                            handleClearError(setErrors, "otp");
                          }}
                        />
                        <ErrorMessage message={errors?.otp} />
                      </div>
                      {/* <p className="text-danger fw-bold">{demoOtp}</p> */}
                    </>
                  )}

                  {/* Submit button */}
                  <div className="mb-3 mt-0">
                    {step === "email" ? (
                      <button
                        type="submit"
                        className="btn btn-primary w-100 "
                        onClick={handleSendOtp}
                      >
                        {forgotPasswordAPI.loading
                          ? "Sending OTP…"
                          : "Send OTP"}
                      </button>
                    ) : (
                      <button
                        type="submit"
                        className="btn btn-primary w-100 py1 fs15"
                        onClick={handleOTPVerify}
                        disabled={verifyOtpAPI.loading}
                      >
                        {verifyOtpAPI.loading
                          ? "Verifying…"
                          : "Verify & Continue"}
                      </button>
                    )}
                  </div>

                  {/* Back to login */}
                  <div className="text-center">
                    <Link
                      to={all_routes.login}
                      className="link-secondary small"
                    >
                      <i className="ti ti-arrow-left" /> Back to Sign In
                    </Link>
                  </div>
                </div>

                <div className="mt-3 pb-4 text-center">
                  <p className="mb-0 text-gray-9">
                    © {new Date().getFullYear()} Aupertz. All rights reserved.
                  </p>
                </div>
              </div>
            </div>
          </div>
          {/* end login content */}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
